import React, { useState, useMemo, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { storage } from '../firebase-config'; 
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import axios from 'axios';
import API_BASE_URL from '../utils/apiConfig';
import { locationData, postTypes as BASE_POST_TYPES, SPECIAL_WISHES_POST_TYPE } from '../utils/constants';
import Select from 'react-select';

// 1. Combine all Post Types
const ALL_POST_TYPE_OPTIONS = (() => {
    const baseTypes = ['World', 'India', 'Andhra Pradesh', 'Telangana']; 
    const generalEntry = locationData.find(d => d.__GENERAL__); 
    const generalTypes = generalEntry ? generalEntry.__GENERAL__ : [];
    const finalBasePostTypes = Array.isArray(BASE_POST_TYPES) ? BASE_POST_TYPES : [];
    const specialWishesArray = (SPECIAL_WISHES_POST_TYPE && !finalBasePostTypes.includes(SPECIAL_WISHES_POST_TYPE)) ? [SPECIAL_WISHES_POST_TYPE] : [];

    const uniqueTypes = new Set([
        ...finalBasePostTypes, 
        ...specialWishesArray, 
        ...generalTypes 
    ]);
    
    return Array.from(uniqueTypes);
})();

// Reusable Searchable MultiSelect component
const SearchableMultiSelect = ({ label, options, selected, onChange, placeholder }) => {
    const selectOptions = options.map(opt => typeof opt === 'string' ? { value: opt, label: opt } : opt);
    const selectedValues = selected.map(value => ({ value: value, label: value }));

    const handleChange = (selectedItems) => {
        onChange(selectedItems ? selectedItems.map(item => item.value) : []);
    };

    return (
        <div className="form-group">
            <label>{label}</label>
            <Select
                isMulti
                options={selectOptions}
                value={selectedValues}
                onChange={handleChange}
                placeholder={placeholder}
                styles={{
                    control: (base, state) => ({
                        ...base,
                        minHeight: '45px',
                        borderRadius: '4px',
                        border: state.isFocused 
                            ? '1px solid var(--primary-color)' 
                            : '1px solid #ccc',
                        boxShadow: state.isFocused ? '0 0 0 1px var(--primary-color)' : 'none',
                        margin: '0', 
                        padding: '0 8px', 
                        backgroundColor: '#fff', 
                        cursor: 'default',
                        }),
                    menu: (base) => ({
                        ...base,
                        zIndex: 1000,
                    }),
                    multiValue: (base) => ({
                        ...base,
                        backgroundColor: 'var(--primary-color)',
                        color: 'white',
                    }),
                    multiValueLabel: (base) => ({
                        ...base,
                        color: 'white',
                    }),
                }}
            />
            <p className="select-hint" style={{fontSize: '0.8em', color: '#666'}}>
                Type to search or tap to select multiple.
            </p>
        </div>
    );
};

const CreateAdModal = ({ onClose, onAdCreated, initialData }) => {
    const { currentUser: userProfile, firebaseUser } = useAuth();
    const isEditing = !!initialData?.id; 
    
    // --- STATE INITIALIZATION ---
    const [adType, setAdType] = useState(initialData?.adType || 'text'); 
    const [brandName, setBrandName] = useState(initialData?.sponsor || initialData?.adDetails?.brandName || '');
    const [adText, setAdText] = useState(initialData?.adText || initialData?.adDetails?.adText || '');
    const [linkUrl, setLinkUrl] = useState(initialData?.link || initialData?.adDetails?.linkUrl || '');
    // const [mediaType, setMediaType] = useState('image');
    const [mediaType, setMediaType] = useState(initialData?.mediaType || initialData?.adDetails?.mediaType || 'image');
    const [imageFiles, setImageFiles] = useState([]); 
    const [uploadedImageUrls, setUploadedImageUrls] = useState(initialData?.imageUrls || []);
    // const [uploadedVideoUrl, setUploadedVideoUrl] = useState(initialData?.videoUrl || '');
    const [uploadedVideoUrl, setUploadedVideoUrl] = useState(
    initialData?.videoUrl || initialData?.adDetails?.uploadedImageUrl || initialData?.uploadedImageUrl || ''
);
    
    // NEW: Ad Layout Option State (Defaults to full_card / Story layout)
    const [layoutType, setLayoutType] = useState(initialData?.layoutType || initialData?.adDetails?.layoutType || 'full_card');
    
    // Targeting states
    const [selectedPostTypes, setSelectedPostTypes] = useState(initialData?.targetPostTypes || []);
    const [selectedStates, setSelectedStates] = useState(initialData?.targetStates || []);
    const [selectedDistricts, setSelectedDistricts] = useState(initialData?.targetDistricts || []);
    const [selectedConstituencies, setSelectedConstituencies] = useState(initialData?.targetConstituencies || []);
    const [selectedMandals, setSelectedMandals] = useState(initialData?.targetMandals || []);

    const handleFileChange = async (e) => {
        const files = Array.from(e.target.files);
        if (mediaType === 'image' && files.length > 4) {
            alert("Maximum 4 images allowed.");
            return;
        }
        if (mediaType === 'video') {
            const video = document.createElement('video');
            video.preload = 'metadata';
            video.onloadedmetadata = () => {
                if (video.duration > 240) {
                    alert("Video must be 4 minutes or less.");
                    setImageFiles([]);
                }
            };
            video.src = URL.createObjectURL(files[0]);
        }
        setImageFiles(files);
    };

    // Dynamic price estimation helper
    const totalPrice = useMemo(() => {
        let count = 0;
        if (selectedPostTypes.includes('World')) count += 1;
        if (selectedPostTypes.includes('India')) count += 1;

        const hasStateSelected = selectedPostTypes.some(t => t === 'Andhra Pradesh' || t === 'Telangana');
        if (hasStateSelected) {
            if (selectedMandals.length > 0) {
                count += selectedMandals.length; 
            } else if (selectedConstituencies.length > 0) {
                count += selectedConstituencies.length; 
            } else if (selectedDistricts.length > 0) {
                count += selectedDistricts.length; 
            } else {
                count += 1; 
            }
        }
        return count * 100;
    }, [selectedPostTypes, selectedDistricts, selectedConstituencies, selectedMandals]);

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        if (isEditing) {
            console.log("Modal opened in Edit Mode for Ad:", initialData.id);
        }
    }, [isEditing, initialData]);

    const dynamicOptions = useMemo(() => {
        let districts = [];
        let constituencies = [];
        let mandals = [];

        const activeStates = selectedPostTypes.filter(t => t === 'Andhra Pradesh' || t === 'Telangana');
        const filteredStates = locationData.filter(d => activeStates.includes(d.state_en));

        filteredStates.forEach(state => {
            state.districts.forEach(d => {
                districts.push({ value: d.district_en, label: d.district_te });
                if (selectedDistricts.includes(d.district_en)) {
                    d.constituencies?.forEach(c => constituencies.push({ value: c.value, label: c.label }));
                    d.mandals?.forEach(m => mandals.push({ value: m.value, label: m.label, parentConstituency: m.constituency }));
                }
            });
        });

        return {
            districtOptions: districts,
            constituencyOptions: constituencies,
            mandalOptions: selectedConstituencies.length > 0 ? mandals : []
        };
    }, [selectedPostTypes, selectedDistricts, selectedConstituencies]);

    if (!firebaseUser) { 
        return <div className="modal-overlay"><div className="modal-content">Please log in to post an ad.</div></div>;
    }

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (totalPrice === 0) { setError("Please select at least one Target area."); return; }
        setLoading(true);
        setError('');

        if (!adText && imageFiles.length === 0 && !uploadedImageUrls.length && !uploadedVideoUrl) {
            setError("You must provide either Ad Text/Slogan or upload an Ad Media.");
            setLoading(false);
            return;
        }
        if (selectedPostTypes.length === 0) {
            setError("Please select at least one Post Type where your ad should appear.");
            setLoading(false);
            return;
        }

        try {
            let finalImageUrls = [...uploadedImageUrls];
            let finalVideoUrl = uploadedVideoUrl;

            if (imageFiles.length > 0) {
                const uploadPromises = imageFiles.map(async (file) => {
                    const storageRef = ref(storage, `advertisements/${Date.now()}_${file.name}`);
                    await uploadBytes(storageRef, file);
                    return await getDownloadURL(storageRef);
                });

                const newUrls = await Promise.all(uploadPromises);
                
                if (mediaType === 'video') {
                    finalVideoUrl = newUrls[0]; 
                } else {
                    finalImageUrls = isEditing ? [...finalImageUrls, ...newUrls] : newUrls;
                }
            }

            let finalPostTypes = [...selectedPostTypes];
            const isLocallyTargeted = selectedDistricts.length > 0 || selectedConstituencies.length > 0 || selectedMandals.length > 0;
            if (isLocallyTargeted) {
                finalPostTypes = finalPostTypes.filter(type => type !== 'Andhra Pradesh' && type !== 'Telangana');
            }

            const targetingPayload = { 
                postTypes: finalPostTypes, 
                states: isLocallyTargeted ? [] : selectedPostTypes.filter(t => t === 'Andhra Pradesh' || t === 'Telangana'),
                districts: selectedDistricts,
                constituencies: selectedConstituencies,
                mandals: selectedMandals
            };

            const adDetails = { 
                linkUrl, 
                adText, 
                brandName, 
                uploadedImageUrls: finalImageUrls, 
                uploadedImageUrl: finalVideoUrl, 
                mediaType,
                layoutType // Included in database update
            };

            const idToken = await firebaseUser.getIdToken(true); 

            if (isEditing) {
                await axios.put(`${API_BASE_URL}/ads/${initialData.id}`, { ...adDetails, targeting: targetingPayload }, {
                    headers: { Authorization: `Bearer ${idToken}` }
                });
            } else {
                const orderRes = await axios.post(`${API_BASE_URL}/ads/create-order`, { amount: totalPrice }, {
                    headers: { Authorization: `Bearer ${idToken}` }
                });

                const paymentDetails = {
                    razorpay_order_id: orderRes.data.id,
                    razorpay_payment_id: "test_pay_" + Date.now(), 
                    razorpay_signature: "test_signature_" + Date.now(), 
                };

                await axios.post(`${API_BASE_URL}/ads/verify-payment`, {
        paymentDetails,
        adDetails, // Passes adDetails as an object
        targeting: targetingPayload
    }, { headers: { Authorization: `Bearer ${idToken}` } });
}
            onClose(); 
            if (onAdCreated) onAdCreated(); 
        } catch (err) {
            setError(`Error: ${err.response?.data?.message || err.message}`);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="modal-overlay">
            <div className="modal-content">
                <div className="modal-header">
                    <h2>Post New Advertisement</h2>
                    <button onClick={onClose}>&times;</button>
                </div>
                <div className="modal-body">
                    {error && <p className="error-message" style={{color: 'red'}}>{error}</p>}
                    <form onSubmit={handleSubmit}>

                        <div className="form-group">
                            <label>Brand/Sponsor Name</label>
                            <input type="text" value={brandName} onChange={(e) => setBrandName(e.target.value)} placeholder="e.g. My Brand" required />
                        </div>

                        <div className="form-group">
                            <label>Destination Link (URL) - Optional</label>
                            <input type="url" value={linkUrl} onChange={(e) => setLinkUrl(e.target.value)} placeholder="https://mybrand.com/offer" />
                        </div>

                        {/* --- NEW: VISUAL LAYOUT SELECTOR OPTIONS --- */}
                        <div className="form-group" style={{ marginBottom: '20px' }}>
                            <label style={{ fontWeight: 'bold', display: 'block', marginBottom: '8px' }}>Ad Card Layout Format</label>
                            <div className="layout-type-selection" style={{ display: 'flex', gap: '15px' }}>
                                
                                {/* Option A: Standalone Full Page Card */}
                                <label 
                                    className={`layout-option-item ${layoutType === 'full_card' ? 'selected' : ''}`}
                                    style={{
                                        flex: 1,
                                        border: layoutType === 'full_card' ? '2px solid var(--primary-color)' : '1px solid #ccc',
                                        borderRadius: '6px',
                                        padding: '12px',
                                        cursor: 'pointer',
                                        backgroundColor: layoutType === 'full_card' ? 'rgba(255, 152, 0, 0.05)' : '#fff',
                                        transition: 'all 0.2s ease',
                                        textAlign: 'center'
                                    }}
                                >
                                    <input 
                                        type="radio" 
                                        name="layoutType" 
                                        value="full_card" 
                                        checked={layoutType === 'full_card'} 
                                        onChange={() => setLayoutType('full_card')}
                                        style={{ marginRight: '8px' }}
                                    />
                                    <strong style={{ display: 'block', fontSize: '13px' }}>నిలువు ప్రకటన (Vertical Ad - 4:5 Ratio)</strong>
                                    <span style={{ fontSize: '11px', color: '#666', display: 'block', marginTop: '4px' }}>
                                        ఇది పూర్తి స్క్రీన్‌లో నిలువుగా కనిపించే ప్రకటన. సాధారణ వార్తల తరహాలోనే స్క్రీన్ మొత్తం కనిపిస్తుంది.
                                    </span>
                                </label>

                                {/* Option B: Split top-banner with recommendations list */}
                                <label 
                                    className={`layout-option-item ${layoutType === 'banner' ? 'selected' : ''}`}
                                    style={{
                                        flex: 1,
                                        border: layoutType === 'banner' ? '2px solid var(--primary-color)' : '1px solid #ccc',
                                        borderRadius: '6px',
                                        padding: '12px',
                                        cursor: 'pointer',
                                        backgroundColor: layoutType === 'banner' ? 'rgba(255, 152, 0, 0.05)' : '#fff',
                                        transition: 'all 0.2s ease',
                                        textAlign: 'center'
                                    }}
                                >
                                    <input 
                                        type="radio" 
                                        name="layoutType" 
                                        value="banner" 
                                        checked={layoutType === 'banner'} 
                                        onChange={() => setLayoutType('banner')}
                                        style={{ marginRight: '8px' }}
                                    />
                                    <strong style={{ display: 'block', fontSize: '13px' }}>అడ్డ ప్రకటన (Horizontal Ad - 16:9 Ratio)</strong>
                                    <span style={{ fontSize: '11px', color: '#666', display: 'block', marginTop: '4px' }}>
                                        ఇది స్క్రీన్ పైభాగంలో అడ్డంగా కనిపించే ప్రకటన.
                                    </span>
                                </label>

                            </div>
                        </div>

                        <div className="form-group">
                            <label>Ad Text/Slogan (Optional)</label>
                            <textarea
                                value={adText}
                                onChange={(e) => setAdText(e.target.value)}
                                placeholder="e.g. Get 50% off on all products for a limited time!"
                            />
                            <p className="select-hint">If provided, this text will be used to generate/overlay on the ad image.</p>
                        </div>

                        <div className="media-toggle" style={{display: 'flex', gap: '10px', marginBottom: '15px'}}>
                            <button 
                                type="button" 
                                className={mediaType === 'image' ? 'active-tab' : ''} 
                                onClick={() => { setMediaType('image'); setImageFiles([]); }}
                            >
                                Images (Max 4)
                            </button>
                            <button 
                                type="button" 
                                className={mediaType === 'video' ? 'active-tab' : ''} 
                                onClick={() => { setMediaType('video'); setImageFiles([]); }}
                            >
                                Video (Max 4 min)
                            </button>
                        </div>

                        <div className="form-group">
                            <label>Upload Media</label>
                            <input 
                                type="file" 
                                accept={mediaType === 'video' ? "video/*" : "image/*"} 
                                multiple={mediaType === 'image'} 
                                onChange={handleFileChange} 
                            />
                            <p className="limit-hint" style={{ color: '#d32f2f', fontSize: '12px', marginTop: '5px', fontWeight: 'bold' }}>
                                {mediaType === 'video' 
                                    ? "⚠️ Video limit: Maximum 4 minutes." 
                                    : "⚠️ Image limit: Maximum 4 images."}
                            </p>
                        </div>
                        
                        <h3 style={{marginTop: '20px'}}>Targeting Options</h3>
                        
                        <SearchableMultiSelect 
                            label="Target Post Types (REQUIRED)"
                            options={ALL_POST_TYPE_OPTIONS}
                            selected={selectedPostTypes}
                            onChange={(val) => {
                                setSelectedPostTypes(val);
                                if (!val.includes('Andhra Pradesh') && !val.includes('Telangana')) {
                                    setSelectedDistricts([]); setSelectedConstituencies([]); setSelectedMandals([]);
                                }
                            }}
                        />

                        {selectedPostTypes.some(t => t === 'Andhra Pradesh' || t === 'Telangana') && (
                            <SearchableMultiSelect 
                                label="Target Districts"
                                options={dynamicOptions.districtOptions}
                                selected={selectedDistricts}
                                onChange={(val) => { setSelectedDistricts(val); setSelectedConstituencies([]); setSelectedMandals([]); }}
                            />
                        )}

                        {selectedDistricts.length > 0 && (
                            <SearchableMultiSelect 
                                label="Target Constituencies (Optional)"
                                options={dynamicOptions.constituencyOptions.map(c => c.value)}
                                selected={selectedConstituencies}
                                onChange={setSelectedConstituencies}
                                placeholder="Search Constituencies..."
                            />
                        )}

                        {selectedConstituencies.length > 0 && (
                            <SearchableMultiSelect 
                                label="Target Mandals (Optional)"
                                options={dynamicOptions.mandalOptions.map(m => m.value)}
                                selected={selectedMandals}
                                onChange={setSelectedMandals}
                                placeholder="Search Mandals..."
                            />
                        )}

                        <div className="payment-warning" style={{
                            marginTop: '20px', 
                            padding: '10px', 
                            border: '1px solid #dc3545', 
                            backgroundColor: '#f8d7da', 
                            color: '#721c24',
                            borderRadius: '4px',
                            fontSize: '0.9em'
                        }}>
                            <strong>Important:</strong> All ad payments are **non-refundable**. Once you post the ad, the amount cannot be returned, even if you delete the ad before the 30-day period ends but you can edit in your profile if you want to make changes in Ad.
                        </div>

                        <div className="modal-footer" style={{marginTop: '20px'}}>
                            <div className="price-display" style={{fontSize: '1.2rem', fontWeight: 'bold', color: '#ff9800', margin: '15px 0'}}>
                                Total Amount: ₹{totalPrice}
                            </div>
                            <button type="button" className="button-secondary" onClick={onClose} disabled={loading}>Cancel</button>
                            <button type="submit" className="button-primary" disabled={loading}>
                                {loading ? 'Processing...' : `Pay ₹${totalPrice} & Post`}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default CreateAdModal;